import { useEffect } from "react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { z } from "zod";

import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import { formatPricingRequestNumber } from "@/api/procurement/procurement.types";
import { PricingDetailPage } from "@/components/features/Procurement/design/PricingDetailPage";
import { useGetProcurementPricingRequestDetail } from "@/hooks/procurement/useProcurement";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getRouteIcon } from "@/utils/getRouteIconComponent";

const detailSearchSchema = z.object({
  tab: z.enum(["request", "options", "answer", "activity"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_authenticated/procurement/pricing/$pricingRequestId")({
  component: ProcurementPricingDetailPage,
  validateSearch: detailSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function ProcurementPricingDetailPage() {
  const { pricingRequestId } = Route.useParams();
  const { tab } = useSearch({ from: "/_authenticated/procurement/pricing/$pricingRequestId" });
  const { data: request } = useGetProcurementPricingRequestDetail(pricingRequestId);
  const navigate = useNavigate();

  /*
   * SERV-2975: a request that has a queue ticket is read on that ticket, where its lane renders
   * alongside the shared header - priority, SLA, financials, approvals. This route survives only
   * for requests raised before the two were linked, which have no ticket to send anyone to.
   *
   * Redirected rather than deleted because the link is already out there: the queue, the sidebar
   * list and anything anyone bookmarked while this was the officer's screen.
   */
  useEffect(() => {
    if (!request?.requestId) return;
    void navigate({
      to: "/procurement/$requestId",
      params: { requestId: request.requestId },
      /* SERV-2975: the ticket can hold several pricing lines; land on this one, not the first. */
      search: { line: pricingRequestId, tab },
      replace: true,
    });
  }, [request?.requestId, pricingRequestId, tab, navigate]);

  const requestLabel = request ? formatPricingRequestNumber(request) : pricingRequestId;

  useBreadcrumb([
    {
      title: "Procurement",
      href: "/procurement",
      icon: getRouteIcon(MODULE_KEYS_MAP.procurement),
    },
    { title: "Pricing Requests", href: "/procurement/pricing" },
    { title: requestLabel, href: `/procurement/pricing/${pricingRequestId}` },
  ]);

  return <PricingDetailPage pricingRequestId={pricingRequestId} initialTab={tab} />;
}
